// src/lib/permissions.ts
/**
 * أدوات الصلاحيات — تخزين، جلب، والتحقق من صلاحيات المستخدم.
 *
 * تُخزَّن بيانات المستخدم (بما فيها الصلاحيات) في localStorage بعد تسجيل الدخول.
 * تُستخدم للتحكم في عناصر الواجهة (إخفاء/إظهار).
 */

const USER_KEY = "mc_user";

export type StoredUser = {
  id: number;
  username: string;
  email?: string | null;
  role: string;
  permissions: string[];
};

/** حفظ بيانات المستخدم (تُستدعى بعد تسجيل الدخول) */
export function setStoredUser(user: StoredUser): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  } catch {
    /* ignore */
  }
}

/** جلب بيانات المستخدم المُخزنة */
export function getStoredUser(): StoredUser | null {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(USER_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** مسح بيانات المستخدم (عند تسجيل الخروج) */
export function clearStoredUser(): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(USER_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** هل المستخدم يملك صلاحية معينة؟ */
export function hasPermission(code: string): boolean {
  const user = getStoredUser();
  if (!user) return false;
  // super_admin يملك كل الصلاحيات
  if (user.role === "super_admin") return true;
  return user.permissions.includes(code);
}

/** هل المستخدم يملك أي من الصلاحيات المُحددة؟ */
export function hasAnyPermission(codes: string[]): boolean {
  const user = getStoredUser();
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return codes.some((c) => user.permissions.includes(c));
}

/** هل المستخدم أدمن (super_admin أو moderator)؟ */
export function isAdmin(): boolean {
  const user = getStoredUser();
  if (!user) return false;
  return user.role === "super_admin" || user.role === "moderator";
}

/** جلب قائمة الصلاحيات الحالية */
export function getPermissions(): string[] {
  const user = getStoredUser();
  return user?.permissions ?? [];
}
